import React, { Component } from 'react';
import 'promise-polyfill/src/polyfill';
import { MuiThemeProvider, withStyles } from '@material-ui/core/styles';
import { HashRouter, Route, Switch } from 'react-router-dom';

import DateFnsUtils from '@date-io/date-fns';
import format from 'date-fns/format';
import nb from 'date-fns/locale/nb';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Typography, CircularProgress, Box, Button,
} from '@material-ui/core';
import { doFetch } from '@trdk/style/Redux/fetch.actions';
import Header from './components/header';
import Theme from './theme';
import { getInitialForm } from './store';
import PageContainer from './components/page-container';
import NotFound from './components/not-found';
import MapView from './pages/map-view';
import Navbar from './components/navbar';
import License from './pages/license';
import Control from './pages/contol';
import NewControl from './pages/new-control';
import Assignment from './pages/assignment';
import WorkPlan from './pages/work-plan';
import NewWorkPlan from './pages/new-work-plan';
import EditWorkPlan from './pages/edit-work-plan';
import GoBackBar from './components/go-back-bar';
import {
  fetchProfile, fetchLicenses, fetchAssignments, fetchControllers, fetchVerify,
  logout,
} from './services/fetch/load-form-service';
import { overwriteFormAction } from './components/submit/submit.actions';


class LocalizedUtils extends DateFnsUtils {
  getDatePickerHeaderText(date) {
    return format(date, 'd. MMMM yyyy', { locale: this.locale });
  }
}

const routes = {
  '/': {
    component: MapView,
    title: '',
    subtitle: '',
  },
  '/oppdrag--:id': {
    component: Assignment,
    title: 'Kontrolloppdrag',
    subtitle: '',
  },
  '/oppdrag--:assignmentID/ny-arbeidsplan': {
    component: NewWorkPlan,
    title: 'Ny arbeidsplan',
    subtitle: '',
  },
  '/oppdrag--:assignmentID/arbeidsplan--:workPlanID': {
    component: WorkPlan,
    title: 'Arbeidsplan',
    subtitle: '',
  },
  '/oppdrag--:assignmentID/arbeidsplan--:workPlanID/rediger': {
    component: EditWorkPlan,
    title: 'Rediger arbeidsplan',
    subtitle: '',
  },
  '/:id': {
    component: License,
    title: '',
    subtitle: '',
  },
  '/:licenseID/ny-kontroll': {
    component: NewControl,
    title: 'Ny kontroll',
    subtitle: '',
  },
  '/:licenseID/kontroll--:controlID': {
    component: Control,
    title: 'Kontroll',
    subtitle: '',
  },
};

class Page extends Component {
  componentDidMount() {
    const {
      dispatch,
      profile: { fetching: profileFeching, fetched: profileFetched },
      isLoggedIn: { fetching: isLoggedInFeching, fetched: isLoggedInFetched },
      licenses: { fetching: licensesFeching, fetched: licensesFetched },
      assignments: { fetching: assignmentsFetching, fetched: assignmentsFetched },
      controllers: { fetching: controllersFetching, fetched: controllersFetched },
    } = this.props;

    const initialState = getInitialForm();
    if (initialState) {
      dispatch(overwriteFormAction(initialState));
    }

    if (!isLoggedInFeching && !isLoggedInFetched) {
      dispatch(doFetch('IS_LOGGEDIN', fetchVerify()));
    }

    this.verifyInterval = setInterval(() => {
      fetchVerify().catch(() => {
        setTimeout(() => {
          dispatch(doFetch('IS_LOGGEDIN', fetchVerify()));
        }, 5000);
      });
    }, 60 * 1000);

    if (!profileFeching && !profileFetched) {
      dispatch(doFetch('PROFILE', fetchProfile()));
    }

    if (!licensesFeching && !licensesFetched) {
      dispatch(doFetch('LICENSES', fetchLicenses()
        .then((res) => {
          if (!res.ok) { throw res; }
          return res.json();
        })));
    }

    if (!assignmentsFetching && !assignmentsFetched) {
      dispatch(doFetch('ASSIGNMENTS', fetchAssignments()
        .then((res) => {
          if (!res.ok) { throw res; }
          return res.json();
        })));
    }

    if (!controllersFetching && !controllersFetched) {
      dispatch(doFetch('CONTROLLERS', fetchControllers()
        .then((res) => {
          if (!res.ok) { throw res; }
          return res.json();
        })));
    }
  }

  componentWillUnmount() {
    clearInterval(this.verifyInterval);
  }

  render() {
    const {
      classes,
      isLoggedIn: {
        fetched: isLoggedInFetched,
        fetching: isLoggedInFetching,
        // error: isLoggedInError,
      },
      profile: {
        fetched: profileFetched,
        fetching: profileFetching,
        // error: profileError,
        payload: profile,
      },
      licenses: {
        fetched: licensesFetched,
        fetching: licensesFetching,
        error: licensesError,
        payload: licenses,
      },
      assignments: {
        fetched: assignmentsFetched,
        fetching: assignmentsFetching,
        error: assignmentsError,
        payload: assignments,
      },
      controllers: {
        fetched: controllersFetched,
        fetching: controllersFetching,
        error: controllersError,
        payload: controllers,
      },
    } = this.props;

    const isLoading = (!isLoggedInFetched && isLoggedInFetching)
      || (!profileFetched && profileFetching)
      || (!licensesFetched && licensesFetching)
      || (!assignmentsFetched && assignmentsFetching)
      || (!controllersFetched && controllersFetching);

    if (!isLoading && isLoggedInFetched && (
      licensesError || assignmentsError || controllersError
      || !licenses || !assignments || !controllers)
    ) {
      if ((licensesError && licensesError.status === 403)
        || (assignmentsError && assignmentsError.status === 403)
        || (controllersError && controllersError.status === 403)) {
        return (
          <Box margin="2rem">
            <Typography variant="h6">Ingen tilgang</Typography>
            <Typography variant="body1" style={{ margin: '1rem 0' }}>
              For å få tilgang til denne siden, må man være registrert som
              skjenkekontrollør i Trondheim kommune.
            </Typography>
            <Typography variant="body1" style={{ margin: '1rem 0' }}>
              {`Du er innlogget med konto: ${profileFetched && profile.email}`}
            </Typography>

            <Button
              color="primary"
              variant="contained"
              onClick={() => logout()}
            >
              Logg ut
            </Button>
          </Box>
        );
      }

      if (licensesError && licensesError.status) {
        return (
          <Box margin="2rem">
            <Typography variant="h6">Feil ved lasting av data</Typography>
            <Typography variant="body1" style={{ margin: '1rem 0' }}>
              {'Det oppsto en feil under henting av bevilling-data fra serveren. '}
              {`Response: ${licensesError.status} ${licensesError.statusText}`}
            </Typography>
          </Box>
        );
      }

      if (assignmentsError && assignmentsError.status) {
        return (
          <Box margin="2rem">
            <Typography variant="h6">Feil ved lasting av data</Typography>
            <Typography variant="body1" style={{ margin: '1rem 0' }}>
              {'Det oppsto en feil under henting av oppdrag-data fra serveren. '}
              {`Response: ${licensesError.status} ${licensesError.statusText}`}
            </Typography>
          </Box>
        );
      }

      if (controllersError && controllersError.status) {
        return (
          <Box margin="2rem">
            <Typography variant="h6">Feil ved lasting av data</Typography>
            <Typography variant="body1" style={{ margin: '1rem 0' }}>
              {'Det oppsto en feil under henting av kontrollør-data fra serveren. '}
              {`Response: ${licensesError.status} ${licensesError.statusText}`}
            </Typography>
          </Box>
        );
      }

      return (<></>);
    }

    return (
      <HashRouter>
        <MuiThemeProvider theme={Theme}>
          <Navbar
            isFetchingProfile={profileFetching}
            hasFetchedProfile={profileFetched}
            profile={profile}
          />
          <MuiPickersUtilsProvider utils={LocalizedUtils} locale={nb}>
            <GoBackBar />
            <Switch>
              {Object.keys(routes).map(path => (
                <Route
                  key={path}
                  path={path}
                  exact
                  component={() => (
                    routes[path].title && routes[path].subtitle && (
                      <Header
                        title={routes[path].title}
                        subtitle={routes[path].subtitle}
                        key="header"
                      />
                    ))}
                />
              ))}
              <Route
                component={() => (
                  <Header
                    title="Finner ikke siden"
                    subtitle="Det ser ut som om innholdet du leter etter har blitt flyttet eller fjernet."
                    key="header"
                  />
                )}
              />
            </Switch>
            <PageContainer>
              {isLoading && (
                <div className={classes.loading}>
                  <CircularProgress />
                </div>
              )}

              {!isLoading && isLoggedInFetched && (
                <Switch>
                  {Object.keys(routes).map(path => (
                    <Route
                      key={path}
                      path={path}
                      exact
                      component={routes[path].component}
                    />
                  ))}
                  <Route component={NotFound} />
                </Switch>
              )}
            </PageContainer>
          </MuiPickersUtilsProvider>
        </MuiThemeProvider>
      </HashRouter>
    );
  }
}


Page.propTypes = {
  classes: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  isLoggedIn: PropTypes.object.isRequired,
  profile: PropTypes.object.isRequired,
  licenses: PropTypes.object.isRequired,
  assignments: PropTypes.object.isRequired,
  controllers: PropTypes.object.isRequired,
};

const styles = theme => ({
  loading: {
    margin: theme.spacing(15),
    textAlign: 'center',
  },
});

const mapStateToProps = ({
  licenses, assignments, controllers, isLoggedIn, profile,
}) => ({
  licenses, assignments, controllers, isLoggedIn, profile,
});

export default connect(mapStateToProps)(withStyles(styles)(Page));
